const CX = window.CX || { routes: {}, strings: {} };
const root = CX.routes.root || '/';
const CART_SECTION = 'cart-drawer';

function toast(message) {
  const el = document.querySelector('[data-cx-toast]');
  if (!el) return;
  el.textContent = `✕ ${message}`;
  el.classList.add('is-visible');
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => el.classList.remove('is-visible'), 1800);
}

function drawer() {
  return document.querySelector('[data-cx-cart-drawer]');
}

function setCount(count) {
  document.querySelectorAll('[data-cx-cart-count]').forEach((el) => {
    el.textContent = count;
  });
}

function renderCart(html) {
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const next = doc.querySelector('[data-cx-cart-contents]');
  const current = document.querySelector('[data-cx-cart-contents]');
  if (next && current) current.innerHTML = next.innerHTML;
  const count = next?.querySelector('[data-cx-cart-count]')?.textContent;
  if (count != null) setCount(count.trim());
}

function openCart() {
  const el = drawer();
  if (el && !el.open) el.showModal();
}

async function postJSON(path, body) {
  const response = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || CX.strings.error);
  return data;
}

async function changeLine(line, quantity) {
  const el = drawer();
  const inDrawer = el?.open;
  el?.setAttribute('aria-busy', 'true');
  try {
    const data = await postJSON('cart/change.js', { line, quantity, sections: [CART_SECTION] });
    if (!inDrawer) {
      window.location.reload();
      return;
    }
    renderCart(data.sections?.[CART_SECTION]);
    setCount(data.item_count);
  } catch (error) {
    toast(error.message);
  } finally {
    el?.removeAttribute('aria-busy');
  }
}

async function addToCart(form) {
  const button = form.querySelector('[type="submit"]');
  const formData = new FormData(form);
  if (!formData.get('id')) return;
  formData.append('sections', CART_SECTION);
  formData.append('sections_url', window.location.pathname);
  button?.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || CX.strings.error);
    renderCart(data.sections?.[CART_SECTION]);
    toast(CX.strings.added);
  } catch (error) {
    toast(error.message);
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-cx-open-cart]');
  if (opener && drawer()) {
    event.preventDefault();
    openCart();
    return;
  }

  if (event.target.closest('[data-cx-close-cart]')) {
    drawer()?.close();
    return;
  }

  const el = drawer();
  if (el?.open && event.target === el) {
    el.close();
    return;
  }

  const lineControl = event.target.closest('[data-cx-line]');
  if (lineControl) {
    event.preventDefault();
    changeLine(Number(lineControl.dataset.cxLine), Number(lineControl.dataset.cxQty));
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-cx-add-form]');
  if (!form) return;
  event.preventDefault();
  addToCart(form);
});

class CxCountdown extends HTMLElement {
  connectedCallback() {
    this.ends = new Date(this.dataset.ends).getTime();
    if (Number.isNaN(this.ends)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, this.ends - Date.now());
    const pad = (n) => String(n).padStart(2, '0');
    const parts = {
      d: pad(Math.floor(left / 864e5)),
      h: pad(Math.floor(left / 36e5) % 24),
      m: pad(Math.floor(left / 6e4) % 60),
      s: pad(Math.floor(left / 1e3) % 60),
    };

    if (this.dataset.format === 'parts') {
      Object.entries(parts).forEach(([unit, value]) => {
        const el = this.querySelector(`[data-unit="${unit}"]`);
        if (el) el.textContent = value;
      });
    } else {
      const el = this.querySelector('[data-short]') || this;
      el.textContent = left === 0 ? CX.strings.live : `${parts.d}D ${parts.h}H`;
    }

    if (left === 0) clearInterval(this.timer);
  }
}

class CxProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-cx-variants]')?.textContent || '[]');
    this.strings = JSON.parse(this.querySelector('[data-cx-strings]')?.textContent || '{}');
    this.fieldsets = [...this.querySelectorAll('[data-cx-option]')];
    this.idInput = this.querySelector('[data-cx-variant-id]');
    this.button = this.querySelector('[data-cx-add-button]');
    this.price = this.querySelector('[data-cx-price]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cx-option] input')) this.update();
    });

    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-media-target]');
      if (tab) this.showMedia(tab.dataset.mediaTarget);
    });

    if (this.fieldsets.length) this.markAvailability(this.selected());
  }

  selected() {
    return this.fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);
  }

  markAvailability(selected) {
    this.fieldsets.forEach((fieldset, index) => {
      fieldset.querySelectorAll('input').forEach((input) => {
        const available = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === input.value &&
            selected.every((value, j) => j === index || value === null || variant.options[j] === value),
        );
        input.toggleAttribute('data-unavailable', !available);
      });
    });
  }

  update() {
    const selected = this.selected();
    this.markAvailability(selected);

    this.fieldsets.forEach((fieldset, index) => {
      const label = fieldset.querySelector('[data-cx-selected-value]');
      if (label && selected[index]) label.textContent = selected[index].toUpperCase();
    });
    const partial = this.variants.find((v) => v.options.every((value, i) => selected[i] === null || value === selected[i])); if (partial && partial.media) this.showMedia(String(partial.media));

    if (selected.includes(null)) {
      this.setButton(this.strings.pick, true);
      this.idInput.value = '';
      return;
    }

    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    if (!variant) {
      this.setButton(this.strings.unavailable, true);
      this.idInput.value = '';
      return;
    }

    this.idInput.value = variant.id;
    if (this.price) this.price.textContent = variant.price;
    this.setButton(variant.available ? `${this.strings.add} · ${variant.price}` : this.strings.soldOut, !variant.available);
    if (variant.media) this.showMedia(String(variant.media));

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  setButton(label, disabled) {
    if (!this.button) return;
    this.button.textContent = label;
    this.button.disabled = disabled;
  }

  showMedia(id) {
    const slide = this.querySelector(`[data-media-id="${id}"]`);
    if (!slide) return;
    this.querySelectorAll('[data-media-id]').forEach((el) => {
      el.hidden = el !== slide;
    });
    this.querySelectorAll('[data-media-target]').forEach((tab) => {
      tab.setAttribute('aria-pressed', String(tab.dataset.mediaTarget === id));
    });
  }
}

class CxRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length || !this.dataset.url) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    observer.observe(this);
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('cx-recommendations');
      if (next?.innerHTML.trim()) this.innerHTML = next.innerHTML;
    } catch {
      this.remove();
    }
  }
}

customElements.define('cx-countdown', CxCountdown);
customElements.define('cx-product', CxProduct);
customElements.define('cx-recommendations', CxRecommendations);
